import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { checkSubdomainSsl } from '@/services/api/chatbot';
import type { ChatbotSubdomain } from '@/types/chatbot';
import { Copy, RefreshCw, CheckCircle2, AlertCircle, ExternalLink, ShieldCheck, ShieldAlert } from 'lucide-react';

const PROXY_HOST = import.meta.env.VITE_CHATBOT_PROXY_HOST || 'chatbot.rocketgroup.io';

interface Props {
    subdomain: ChatbotSubdomain;
    variant?: 'card' | 'inline';   // card = bloco com borda; inline = compacto
    showStatus?: boolean;
}

export default function DnsInstructions({ subdomain, variant = 'card', showStatus = true }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [copied, setCopied] = useState<string | null>(null);

    // estado local sincronizado com a prop - permite refletir o resultado do check-ssl
    // mesmo quando o componente nao depende de uma useQuery externa
    const [current, setCurrent] = useState<ChatbotSubdomain>(subdomain);
    useEffect(() => {
        setCurrent(subdomain);
    }, [subdomain.id, subdomain.ssl_status, subdomain.ssl_checked_at, subdomain.ssl_expires_at]);

    const checkMut = useMutation({
        mutationFn: () => checkSubdomainSsl(current.id),
        onSuccess: (sub) => {
            setCurrent(sub);  // atualiza imediatamente o que esta sendo renderizado
            const ok = sub.ssl_status === 'valid';
            toast({
                title: ok ? 'SSL valido!' : `SSL: ${sub.ssl_status}`,
                description: sub.ssl_last_error || (sub.ssl_expires_at && `Expira: ${sub.ssl_expires_at}`) || undefined,
                variant: ok ? 'default' : 'destructive',
            });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomains'] });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomain', current.id] });
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const copy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(null), 1500);
    };

    const isValid = current.ssl_status === 'valid';
    const wrapperClass = variant === 'card'
        ? 'bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3'
        : 'space-y-3';

    return (
        <div className={wrapperClass}>
            {/* status */}
            {showStatus && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <SslBadge status={current.ssl_status} />
                        {current.ssl_expires_at && (
                            <span className="text-xs text-slate-500">expira: {current.ssl_expires_at}</span>
                        )}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => checkMut.mutate()}
                        disabled={checkMut.isPending}>
                        <RefreshCw className={`w-3 h-3 mr-1 ${checkMut.isPending ? 'animate-spin' : ''}`} />
                        Verificar SSL
                    </Button>
                </div>
            )}

            {!isValid && current.ssl_last_error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {current.ssl_last_error}
                </div>
            )}

            {/* instrucoes DNS - so se nao tiver cert valido */}
            {!isValid && (
                <div className="space-y-3">
                    <div className="text-sm font-semibold text-slate-800">Como configurar o DNS</div>

                    <ol className="space-y-3 text-sm">
                        <li className="flex gap-2">
                            <span className="font-bold text-slate-400">1.</span>
                            <div className="flex-1">
                                <div className="text-slate-700 mb-1.5">
                                    No painel DNS do dominio <span className="font-mono text-xs">{rootDomain(current.subdomain)}</span>, crie um destes registros:
                                </div>

                                {/* CNAME (preferido) */}
                                <div className="bg-white border rounded-md p-3 mb-2">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                                            Opcao 1: CNAME (recomendado)
                                        </div>
                                        <Badge variant="outline" className="text-[10px]">facil</Badge>
                                    </div>
                                    <div className="grid grid-cols-[80px,1fr,auto] gap-2 text-xs items-center">
                                        <span className="text-slate-500">Tipo</span>
                                        <span className="font-mono">CNAME</span>
                                        <span></span>

                                        <span className="text-slate-500">Nome</span>
                                        <span className="font-mono">{subdomainPart(current.subdomain)}</span>
                                        <CopyBtn text={subdomainPart(current.subdomain)} keyId="cname-name" copied={copied} onCopy={copy} />

                                        <span className="text-slate-500">Valor</span>
                                        <span className="font-mono">{PROXY_HOST}</span>
                                        <CopyBtn text={PROXY_HOST} keyId="cname-value" copied={copied} onCopy={copy} />

                                        <span className="text-slate-500">TTL</span>
                                        <span className="font-mono">300 (5 min)</span>
                                        <span></span>
                                    </div>
                                </div>

                                {/* A record (alternativa) */}
                                <details className="bg-white border rounded-md p-3">
                                    <summary className="text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer">
                                        Opcao 2: A record (avancado)
                                    </summary>
                                    <div className="grid grid-cols-[80px,1fr,auto] gap-2 text-xs items-center mt-2">
                                        <span className="text-slate-500">Tipo</span>
                                        <span className="font-mono">A</span>
                                        <span></span>

                                        <span className="text-slate-500">Nome</span>
                                        <span className="font-mono">{subdomainPart(current.subdomain)}</span>
                                        <span></span>

                                        <span className="text-slate-500">Valor</span>
                                        <span className="font-mono text-amber-700">
                                            IP do servidor (consulte time de infra)
                                        </span>
                                        <span></span>
                                    </div>
                                </details>
                            </div>
                        </li>

                        <li className="flex gap-2">
                            <span className="font-bold text-slate-400">2.</span>
                            <div className="flex-1 text-slate-700">
                                Aguarde a propagacao do DNS (1-15 min normalmente).
                                <div className="mt-1.5 text-xs">
                                    Conferir:{' '}
                                    <a href={`https://dnschecker.org/#CNAME/${current.subdomain}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                                        dnschecker.org <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            </div>
                        </li>

                        <li className="flex gap-2">
                            <span className="font-bold text-slate-400">3.</span>
                            <div className="flex-1 text-slate-700">
                                Acesse{' '}
                                <a href={`https://${current.subdomain}/`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="font-mono text-emerald-700 hover:underline">
                                    https://{current.subdomain}
                                </a>{' '}
                                no navegador. O certificado SSL e emitido automaticamente
                                na 1a request (demora 5-10s da primeira vez).
                            </div>
                        </li>

                        <li className="flex gap-2">
                            <span className="font-bold text-slate-400">4.</span>
                            <div className="flex-1 text-slate-700">
                                Clique em <strong>Verificar SSL</strong> acima pra atualizar o status.
                            </div>
                        </li>
                    </ol>
                </div>
            )}

            {isValid && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 rounded flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="font-semibold">Tudo configurado!</div>
                        <div className="text-xs mt-1">
                            Acesse os chats em <span className="font-mono">https://{current.subdomain}/&lt;path&gt;</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CopyBtn({ text, keyId, copied, onCopy }:
    { text: string; keyId: string; copied: string | null; onCopy: (t: string, k: string) => void }) {
    const isCopied = copied === keyId;
    return (
        <button
            onClick={() => onCopy(text, keyId)}
            className={`p-1 rounded hover:bg-slate-100 transition ${isCopied ? 'text-emerald-600' : 'text-slate-400'}`}
            title="Copiar">
            {isCopied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
}

function SslBadge({ status }: { status: ChatbotSubdomain['ssl_status'] }) {
    if (status === 'valid')
        return <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
            <ShieldCheck className="w-3 h-3 mr-1" />SSL valido
        </Badge>;
    if (status === 'pending')
        return <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
            SSL pendente
        </Badge>;
    if (status === 'invalid')
        return <Badge className="bg-red-100 text-red-700 border border-red-200">
            <ShieldAlert className="w-3 h-3 mr-1" />SSL invalido
        </Badge>;
    return <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
        <ShieldAlert className="w-3 h-3 mr-1" />SSL erro
    </Badge>;
}

function subdomainPart(host: string): string {
    // chat.dominio.com -> chat
    // sub.dominio.com.br -> sub
    const parts = host.split('.');
    if (parts.length <= 2) return '@';     // root domain (raro pra chatbot)
    return parts[0];
}

function rootDomain(host: string): string {
    // chat.dominio.com -> dominio.com
    // sub.dominio.com.br -> dominio.com.br
    const parts = host.split('.');
    if (parts.length <= 2) return host;
    // heuristica simples - 2 ultimas partes, ou 3 se a penultima for um TLD composto
    const lastTwo = parts.slice(-2).join('.');
    const lastThree = parts.slice(-3).join('.');
    const compoundTlds = ['com.br', 'com.mx', 'co.uk', 'com.ar', 'org.br'];
    return compoundTlds.includes(lastTwo) ? lastThree : lastTwo;
}
